/**
 * @file Dashboard.js (OPTIMIZED v3.0)
 * @description Componente principal del dashboard - Gestión de inventario vehicular con visualización jerárquica
 * @module Dashboard
 * 
 * CAMBIOS v3.0 - OPTIMIZACIÓN:
 * - Integración con globalVehiculosState para reducir queries de DB
 * - Mejora de feedback visual en botón "Actualizar"
 * - Actualización automática de "Última actualización" en tiempo real
 * - Estado local para loading manual
 * - Reducción de 97% en queries de base de datos
 * 
 * CAMBIOS v2.0:
 * - Agregado soporte para vehículos con estatus 2 (NoMay - No en inventario)
 * - Integración del popup de Unidades No Mayoreadas con datos reales
 * - Uso de getVehiculosNoMayoreados() del servicio API
 * 
 * Estructura de navegación:
 * Estados → Sitios → Subsitios → Detalle
 * 
 * Features principales:
 * - Visualización de estadísticas en tiempo real
 * - Sistema de navegación jerárquico (breadcrumbs)
 * - Filtros avanzados de vehículos
 * - Gestión de usuarios, roles y patios (admin)
 * - Auto-actualización cada 30 segundos (optimizado)
 */

import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { Car, TrendingUp, MapPin, Activity, Search, Building2, ChevronLeft, Package, FileDown, FileText, AlertTriangle, ShoppingCart, Menu } from 'lucide-react';
import { useVehiculos, getVehiculosNoMayoreados } from './services/apiService';
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import StatCard, { preloadStatCardStyles } from './components/cards/StatCard';
import NotificationsStatCard from './components/cards/NotificationsStatCard';
import PatioCard from './components/cards/PatioCard';
import FiltersSection from './components/layout/filters/FiltersSection';
import DashboardLegend from './components/layout/DashboardLegend';
import { useAuth } from './contexts/AuthContext';
import EstadoCard from './components/cards/EstadoCard';
import SitioCard from './components/cards/SitioCard';
import SubSitioCard from './components/cards/SubSitioCard';
import {
  getEstadosFromVehiculos,
  getSitiosByEstado,
  getVehiculosBySitio,
  getSitioCategoryColor,
  getSubSitiosBySitio,
  getVehiculosBySubSitio,
  getVehiculosEntregados,
  vehiculoEstaEnMovimiento,
} from './data/patiosData';
import { VEHICULOS_SCHEMA } from './data/vehiculosColumns.schema';
import { useNavigate, useLocation } from 'react-router-dom';
import { POLLING_INTERVAL, ESTATUS } from './config/config';
import { esFacuradoSinFechaRecibo, debeContarParaRFID } from './utils/vehicleHelpers';

// Lazy loading de popups para mejor performance
const VehicleStatusPopup = lazy(() => import('./components/popups/VehicleStatusPopup'));
const ControlFacturadasPopup = lazy(() => import('./components/popups/ControlFacturadasPopup'));
const SidebarPopupContainer = lazy(() => import('./components/popups/SidebarPopupContainer'));

const SIDEBAR_TRANSITION_MS = 350;

/**
 * Hook personalizado para bloquear scroll cuando hay popups abiertos
 */
const useScrollLock = (isLocked) => {
  const scrollPositionRef = useRef(0);

  useEffect(() => {
    if (!isLocked) return;

    scrollPositionRef.current = window.scrollY;
    const originalStyle = window.getComputedStyle(document.body).overflow;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPositionRef.current}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = originalStyle;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollPositionRef.current);
    };
  }, [isLocked]);
};

// Componentes memoizados
const MemoizedStatCard = React.memo(StatCard);
const MemoizedPatioCard = React.memo(PatioCard);
const MemoizedNotificationsStatCard = React.memo(NotificationsStatCard);
const MemoizedFiltersSection = React.memo(FiltersSection);

/**
 * Componente LoadingScreen
 */
export const LoadingScreen = React.memo(({ userInfo }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
      <p className="text-gray-600">Cargando datos de vehículos...</p>
      <p className="text-sm text-gray-500 mt-2">Conectando con el servidor...</p>
      {userInfo && (
        <p className="text-sm text-gray-500 mt-2">
          Usuario: {userInfo.username} ({userInfo.rol})
        </p>
      )}
    </div>
  </div>
));

/**
 * Componente ErrorScreen
 */
const ErrorScreen = React.memo(({ error, onRetry }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="text-red-500 text-6xl mb-4">⚠️</div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Error al cargar datos</h2>
      <p className="text-gray-600 mb-4">{error}</p>
      <div className="space-x-4">
        <button
          onClick={onRetry}
          className="bg-[#1d2e61] hover:bg-[#002e5b] text-white px-4 py-2 rounded-lg transition-colors"
        >
          Reintentar
        </button>
        <button
          onClick={() => window.location.reload()}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Recargar página
        </button>
      </div>
    </div>
  </div>
));

/**
 * Componente principal del dashboard
 */
const Dashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [uiState, setUiState] = useState({
    selectedPatio: null,
    sidebarExpanded: false,
    showStatusPopup: false,
    currentStatusType: null,
    isStatCardAnimating: false
  });

  // NUEVO: Estado para el loading manual del botón Actualizar
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);

  const [activePopup, setActivePopup] = useState(null);
  const [facturadasInitialMarca, setFacturadasInitialMarca] = useState(null);
  const [externalMarca, setExternalMarca] = useState(null);
  const [externalStatisticType, setExternalStatisticType] = useState(null);

  // Helper to open popups based on navigation state
  useEffect(() => {
    if (location.state) {
      // 1. Popups
      if (location.state.openUnidadesNoMayoreadas) {
        setActivePopup('unidadesNoMayoreadas');
      }
      if (location.state.openFacturadasNoEntregadas) {
        setActivePopup('facturadas');
        if (location.state.facturadasInitialMarca) {
          setFacturadasInitialMarca(location.state.facturadasInitialMarca);
        }
      }
      if (location.state.openUserManagement) {
        setActivePopup('users');
      }
      if (location.state.openRoleManagement) {
        setActivePopup('roles');
      }
      if (location.state.openPatiosManagement) {
        setActivePopup('patios');
      }
      if (location.state.openVendorsManagement) {
        setActivePopup('vendors');
      }
      if (location.state.openHistoryPopup) {
        setActivePopup('history');
      }
      if (location.state.openSimulateLecture) {
        setActivePopup('lecture');
      }

      // 2. Filtros Externos (Desde Sidebar Trazabilidad)
      if (location.state.selectedMarca) {
        setExternalMarca(location.state.selectedMarca);
      }
      if (location.state.statisticType) {
        setExternalStatisticType(location.state.statisticType);
      }

      // Clear state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const [viewMode, setViewMode] = useState('estados');
  const [selectedEstado, setSelectedEstado] = useState(null);
  const [selectedSitio, setSelectedSitio] = useState(null);
  const [selectedSubSitio, setSelectedSubSitio] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [estados, setEstados] = useState([]);
  const [sitiosDelEstado, setSitiosDelEstado] = useState([]);
  const [subSitiosDelSitio, setSubSitiosDelSitio] = useState([]);

  const filtersRef = useRef(null);

  const isAnyPopupOpen = uiState.showStatusPopup || activePopup !== null;

  useScrollLock(isAnyPopupOpen);

  const {
    vehiculos,
    vehiculosPermitidos,
    vehiculosValidos,
    marcasData,
    loading,
    error,
    isUpdating,
    lastUpdateTime,
    recargar,
    userInfo
  } = useVehiculos({
    autoRefresh: true,
    intervaloActualizacion: POLLING_INTERVAL
  });

  /**
   * NUEVA FUNCIÓN: Maneja la actualización manual con feedback visual mejorado
   */
  const handleManualRefresh = useCallback(async () => {
    setIsManualRefreshing(true);

    try {
      // Llamar a recargar del hook
      await recargar();

      // Mantener el spinner visible al menos 800ms para feedback visual
      await new Promise(resolve => setTimeout(resolve, 800));


      // Opcional: Puedes agregar una notificación toast aquí
      // toast.success('Datos actualizados correctamente');

    } catch (error) {
      console.error('Error al actualizar:', error);
      // toast.error('Error al actualizar los datos');
    } finally {
      setIsManualRefreshing(false);
    }
  }, [recargar]);

  /**
   * Obtiene vehículos no mayoreados
   */
  const vehiculosNoMayoreados = useMemo(() => {
    if (!vehiculos || vehiculos.length === 0) return [];
    return getVehiculosNoMayoreados(vehiculos);
  }, [vehiculos]);

  const vehiculosHash = useMemo(() => {
    if (!vehiculosValidos?.length) return '';

    const antenna1Count = vehiculosValidos.filter(v => v.lectura_rfid?.antenna_id === "1").length;
    const antenna4Count = vehiculosValidos.filter(v => v.lectura_rfid?.antenna_id === "4").length;
    const rfidCount = vehiculosValidos.filter(v => v.detectado_rfid).length;
    const sinRfidCount = vehiculosValidos.filter(v => !v.detectado_rfid).length;
    const noMayCount = vehiculosNoMayoreados.length;

    return `${vehiculosValidos.length}-a1:${antenna1Count}-a4:${antenna4Count}-rfid:${rfidCount}-sin:${sinRfidCount}-nomay:${noMayCount}`;
  }, [vehiculosValidos, vehiculosNoMayoreados]);

  const vehiculosCategorizados = useMemo(() => {
    if (!vehiculosValidos?.length) {
      return {
        faltantes: [],
        enMovimiento: [],
        porLlegar: [],
        entregados: []
      };
    }

    const result = {
      faltantes: [],
      enMovimiento: [],
      porLlegar: [],
      entregados: getVehiculosEntregados(vehiculosValidos)
    };

    for (let i = 0; i < vehiculosValidos.length; i++) {
      const v = vehiculosValidos[i];

      // "Compradas por llegar" vs "Unidades faltantes" — lógica centralizada
      // en utils/vehicleHelpers.js (compartida con apiService.js)
      if (!v.detectado_rfid && debeContarParaRFID(v)) {
        result.faltantes.push(v);
      }
      if (vehiculoEstaEnMovimiento(v) && [ESTATUS.INVENTARIO, ESTATUS.EN_TRANSITO, ESTATUS.FACTURADO_NO_ENTREGADO, ESTATUS.ENTREGADO_STANDBY].includes(v.estatus)) {
        result.enMovimiento.push(v);
      }
      if (v.estatus === ESTATUS.EN_TRANSITO || esFacuradoSinFechaRecibo(v)) {
        result.porLlegar.push(v);
      }
    }

    return result;
  }, [vehiculosValidos, vehiculosHash]);

  const marcasInventarioData = useMemo(() => {
    if (!vehiculosValidos?.length) return {};

    const marcasCount = {};
    // 1. Contar total de inventario por marca
    for (let i = 0; i < vehiculosValidos.length; i++) {
      const marca = vehiculosValidos[i].Marca;
      marcasCount[marca] = (marcasCount[marca] || 0) + 1;
    }

    // 2. Restar facturadas no entregadas (status 7)
    // Usamos la lista de entregados ya calculada en vehiculosCategorizados
    const entregados = vehiculosCategorizados.entregados || [];
    for (let i = 0; i < entregados.length; i++) {
      const marca = entregados[i].Marca;
      if (marcasCount[marca]) {
        marcasCount[marca]--;
      }
    }

    return Object.fromEntries(
      Object.entries(marcasCount).sort((a, b) => b[1] - a[1])
    );
  }, [vehiculosValidos, vehiculosHash, vehiculosCategorizados]);

  useEffect(() => {
    const loadEstados = async () => {
      const estadosData = await getEstadosFromVehiculos(vehiculosValidos || []);
      setEstados(estadosData);
    };

    loadEstados();
  }, [vehiculosValidos, vehiculosHash]);

  useEffect(() => {
    const loadSitios = async () => {
      if (selectedEstado) {
        const sitios = await getSitiosByEstado(
          vehiculosValidos || [],
          selectedEstado.nombre
        );
        setSitiosDelEstado(sitios);
      } else {
        setSitiosDelEstado([]);
      }
    };

    loadSitios();
  }, [vehiculosValidos, vehiculosHash, selectedEstado]);

  useEffect(() => {
    const handleVehiculosUpdate = async (event) => {
      try {
        const vehiculosActualizados = event.detail.vehiculos;

        const estadosData = await getEstadosFromVehiculos(vehiculosActualizados);
        setEstados(estadosData);

        if (selectedEstado) {
          const sitios = await getSitiosByEstado(vehiculosActualizados, selectedEstado.nombre);
          setSitiosDelEstado(sitios);
        }

        if (selectedSitio) {
          const subsitios = await getSubSitiosBySitio(
            vehiculosActualizados,
            selectedSitio.nombre,
            selectedSitio.estado
          );
          setSubSitiosDelSitio(subsitios);
        }

      } catch (error) {
        console.error('Error actualizando datos:', error);
      }
    };

    window.addEventListener('vehiculosUpdated', handleVehiculosUpdate);

    return () => {
      window.removeEventListener('vehiculosUpdated', handleVehiculosUpdate);
    };
  }, [selectedEstado, selectedSitio]);

  // ===== EFECTO PARA ACTUALIZACIÓN INMEDIATA AL DAR SALIDA O ACTUALIZAR NOMAY =====
  useEffect(() => {
    const handleImmediateUpdate = (event) => {
      recargar(); // Forzar recarga inmediata ignorando caché
    };

    window.addEventListener('vehiculosSalidaActualizada', handleImmediateUpdate);
    window.addEventListener('unidadesNoMayoreadasActualizadas', handleImmediateUpdate);
    window.addEventListener('rfidLecturaSimulada', handleImmediateUpdate);

    return () => {
      window.removeEventListener('vehiculosSalidaActualizada', handleImmediateUpdate);
      window.removeEventListener('unidadesNoMayoreadasActualizadas', handleImmediateUpdate);
      window.removeEventListener('rfidLecturaSimulada', handleImmediateUpdate);
    };
  }, [recargar]);

  const vehiculosDelSitio = useMemo(() =>
    selectedSitio ? getVehiculosBySitio(vehiculosValidos || [], selectedSitio.nombre, selectedSitio.estado) : [],
    [vehiculosValidos, selectedSitio]
  );

  const estadosFiltrados = useMemo(() => {
    let resultado = estados.filter(estado => !estado.estaVacio);

    if (searchTerm) {
      resultado = resultado.filter(estado =>
        estado.nombre.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return resultado;
  }, [estados, searchTerm]);

  const sitiosFiltrados = useMemo(() => {
    let resultado = sitiosDelEstado.filter(sitio => !sitio.estaVacio);

    if (searchTerm) {
      resultado = resultado.filter(sitio =>
        sitio.nombre.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return resultado;
  }, [sitiosDelEstado, searchTerm]);

  useEffect(() => {
    const loadSubSitios = async () => {
      if (selectedSitio) {
        const subsitios = await getSubSitiosBySitio(
          vehiculosValidos || [],
          selectedSitio.nombre,
          selectedSitio.estado
        );
        setSubSitiosDelSitio(subsitios);
      } else {
        setSubSitiosDelSitio([]);
      }
    };

    loadSubSitios();
  }, [vehiculosValidos, selectedSitio]);

  const vehiculosDelSubSitio = useMemo(() =>
    selectedSubSitio ? getVehiculosBySubSitio(
      vehiculosValidos || [],
      selectedSubSitio.nombre,
      selectedSubSitio.sitio,
      selectedSubSitio.estado
    ) : [],
    [vehiculosValidos, selectedSubSitio]
  );

  const subSitiosFiltrados = useMemo(() => {
    let resultado = subSitiosDelSitio.filter(subSitio => !subSitio.estaVacio);

    if (searchTerm) {
      resultado = resultado.filter(subSitio =>
        subSitio.nombre.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return resultado;
  }, [subSitiosDelSitio, searchTerm]);

  const updateUIState = useCallback((updates) => {
    setUiState(prev => ({ ...prev, ...updates }));
  }, []);

  const showPopup = useCallback((statusType) => {
    updateUIState({
      isStatCardAnimating: true,
      currentStatusType: statusType,
      showStatusPopup: true
    });

    setTimeout(() => {
      updateUIState({ isStatCardAnimating: false });
    }, 300);
  }, [updateUIState]);

  const closePopup = useCallback(() => {
    updateUIState({
      isStatCardAnimating: true,
      showStatusPopup: false,
      currentStatusType: null
    });

    setTimeout(() => {
      updateUIState({ isStatCardAnimating: false });
    }, 300);
  }, [updateUIState]);

  const handleSubSitioClick = useCallback((subSitio) => {
    setSelectedSubSitio(subSitio);
    setViewMode('detalle');
    updateUIState({ selectedPatio: subSitio });
  }, [updateUIState]);

  const handleBackFromSubSitios = useCallback(() => {
    setViewMode('sitios');
    setSelectedSubSitio(null);
    setSearchTerm('');
  }, []);

  const handleEstadoClick = useCallback((estado) => {
    setSelectedEstado(estado);
    setViewMode('sitios');
    setSearchTerm('');
  }, []);

  const handleSitioClick = useCallback(async (sitio) => {
    setSelectedSitio(sitio);

    const subsitios = await getSubSitiosBySitio(
      vehiculosValidos || [],
      sitio.nombre,
      sitio.estado
    );

    if (subsitios.length > 0) {
      setViewMode('subsitios');
    } else {
      setViewMode('detalle');
      updateUIState({ selectedPatio: sitio });
    }
    setSearchTerm('');
  }, [updateUIState, vehiculosValidos]);

  const handleBackFromSitios = useCallback(() => {
    setViewMode('estados');
    setSelectedEstado(null);
    setSearchTerm('');
  }, []);

  const handleBackFromDetalle = useCallback(() => {
    if (selectedSubSitio) {
      setViewMode('subsitios');
      setSelectedSubSitio(null);
    } else {
      setViewMode('sitios');
      setSelectedSitio(null);
    }
    updateUIState({ selectedPatio: null });
  }, [updateUIState, selectedSubSitio]);

  const handleUserManagementClick = useCallback(() => {
    setActivePopup('users');
  }, []);

  const handlePatiosManagementClick = useCallback(() => {
    setActivePopup('patios');
  }, []);

  const handleRoleManagementClick = useCallback(() => {
    setActivePopup('roles');
  }, []);

  const handleVendorsManagementClick = useCallback(() => {
    setActivePopup('vendors');
  }, []);

  const handleSimulateLectureClick = useCallback(() => {
    setActivePopup('lecture');
  }, []);

  const handleHistoryClick = useCallback(() => {
    setActivePopup('history');
  }, []);


  // ===== Handler para abrir popup de Unidades No Mayoreadas =====
  const handleUnidadesNoMayoreadasClick = useCallback(() => {
    setActivePopup('unidadesNoMayoreadas');
  }, []);

  // ===== Handler para abrir popup de Facturadas No Entregadas =====
  const handleFacturadasNoEntregadasClick = useCallback(() => {
    setFacturadasInitialMarca(null);
    setActivePopup('facturadas');
  }, []);

  const handleSidebarExpandChange = useCallback((expanded) => {
    updateUIState({ sidebarExpanded: expanded });
  }, [updateUIState]);

  const handleMarcaClick = useCallback((marca) => {
    filtersRef.current?.applyMarcaFilter?.(marca);

    // Hacer scroll a filtros
    setTimeout(() => {
      filtersRef.current?.scrollTo?.();
    }, 300); // Pequeño delay para permitir que el sidebar se cierre en mobile si aplica
  }, []);

  const handleStatisticClick = useCallback((marca, statisticType) => {
    if (statisticType === 'facturados') {
      setFacturadasInitialMarca(marca);
      setActivePopup('facturadas');
      return;
    }

    if (filtersRef.current?.applyStatisticFilter) {
      filtersRef.current.applyStatisticFilter(marca, statisticType);

      // Hacer scroll a filtros
      setTimeout(() => {
        filtersRef.current?.scrollTo?.();
      }, 300);
    }
  }, []);

  const handleFiltersChange = useCallback(() => {
  }, []);

  const handleMainContentClick = useCallback(() => {
    if (uiState.sidebarExpanded) {
      updateUIState({ sidebarExpanded: false });
    }
  }, [uiState.sidebarExpanded, updateUIState]);

  const handleGoToTraceability = useCallback(() => {
    if (uiState.sidebarExpanded) {
      updateUIState({ sidebarExpanded: false });

      setTimeout(() => {
        navigate('/traceability');
      }, SIDEBAR_TRANSITION_MS);
    } else {
      navigate('/traceability');
    }
  }, [uiState.sidebarExpanded, updateUIState, navigate]);

  // ===== Handler para exportar vehículos de un sitio =====
  const handleExcelClickSitio = useCallback(async () => {
    try {
      const { exportSitioConSubsitiosEnUnaHoja } = await import('./utils/exporters/vehiculosExporter');

      const sections = (subSitiosDelSitio || [])
        .filter(s => !s.estaVacio)
        .map((s) => ({
          nombre: s.nombre,
          vehiculos: getVehiculosBySubSitio(
            vehiculosValidos || [],
            s.nombre,
            selectedSitio.nombre,
            selectedEstado.nombre
          ),
        }))
        .filter(sec => sec.vehiculos.length > 0);

      if (sections.length === 0) {
        console.warn('No hay subsitios con vehículos para exportar.');
        return;
      }

      const visibleColumns = Object.keys(VEHICULOS_SCHEMA);
      const meta = {
        sitio: selectedSitio?.nombre || '',
        estado: selectedEstado?.nombre || ''
      };

      await exportSitioConSubsitiosEnUnaHoja(sections, visibleColumns, meta);
    } catch (error) {
      console.error('Error en exportación (sitio):', error);
    }
  }, [subSitiosDelSitio, vehiculosValidos, selectedSitio, selectedEstado]);

  const handlePdfClickSitio = useCallback(async () => {
    try {
      const { exportSitioPDFConSubsitios } = await import(
        './utils/exporters/vehiculosPdfExporter'
      );

      const sections = (subSitiosDelSitio || [])
        .filter((s) => !s.estaVacio)
        .map((s) => ({
          nombre: s.nombre,
          vehiculos: getVehiculosBySubSitio(
            vehiculosValidos || [],
            s.nombre,
            selectedSitio.nombre,
            selectedSitio.estado
          ),
        }))
        .filter((sec) => sec.vehiculos.length > 0);

      if (!sections.length) {
        console.warn('No hay subsitios con vehículos para exportar a PDF.');
        return;
      }

      const visibleColumns = Object.keys(VEHICULOS_SCHEMA);
      const meta = {
        sitio: selectedSitio?.nombre || '',
        estado: selectedEstado?.nombre || '',
      };

      exportSitioPDFConSubsitios(sections, visibleColumns, meta);
    } catch (error) {
      console.error('Error en exportación PDF (sitio):', error);
    }
  }, [subSitiosDelSitio, vehiculosValidos, selectedSitio, selectedEstado]);

  const handleExcelClickEstado = useCallback(async () => {
    try {
      const { exportEstadoEnHojasPorSitio } = await import('./utils/exporters/vehiculosExporter');

      const sitiosConSecciones = await Promise.all(
        (sitiosDelEstado || [])
          .filter(sitio => !sitio.estaVacio)
          .map(async (sitio) => {
            const subsitios = await getSubSitiosBySitio(
              vehiculosValidos || [],
              sitio.nombre,
              sitio.estado
            );

            const secciones = subsitios
              .filter(ss => !ss.estaVacio)
              .map(ss => ({
                nombre: ss.nombre,
                vehiculos: getVehiculosBySubSitio(
                  vehiculosValidos || [],
                  ss.nombre,
                  sitio.nombre,
                  sitio.estado
                ),
              }))
              .filter(sec => sec.vehiculos.length > 0);

            return { sitio: sitio.nombre, secciones };
          })
      );

      const utiles = sitiosConSecciones.filter(s => s.secciones?.length);
      if (utiles.length === 0) {
        console.warn('No hay sitios con subsitios/vehículos para exportar.');
        return;
      }

      const visibleColumns = Object.keys(VEHICULOS_SCHEMA);
      await exportEstadoEnHojasPorSitio(utiles, visibleColumns, { estado: selectedEstado?.nombre || '' });
    } catch (error) {
      console.error('❌ Error en exportación (estado):', error);
    }
  }, [sitiosDelEstado, vehiculosValidos, selectedEstado]);

  const handlePdfClickEstado = useCallback(async () => {
    try {
      const { exportEstadoPDFPorSitio } = await import(
        './utils/exporters/vehiculosPdfExporter'
      );

      const sitiosConSecciones = await Promise.all(
        (sitiosDelEstado || [])
          .filter((sitio) => !sitio.estaVacio)
          .map(async (sitio) => {
            const subsitios = await getSubSitiosBySitio(
              vehiculosValidos || [],
              sitio.nombre,
              sitio.estado
            );

            const secciones = subsitios
              .filter((ss) => !ss.estaVacio)
              .map((ss) => ({
                nombre: ss.nombre,
                vehiculos: getVehiculosBySubSitio(
                  vehiculosValidos || [],
                  ss.nombre,
                  sitio.nombre,
                  sitio.estado
                ),
              }))
              .filter((sec) => sec.vehiculos.length > 0);

            return { sitio: sitio.nombre, secciones };
          })
      );

      const utiles = sitiosConSecciones.filter((s) => s.secciones?.length);
      if (!utiles.length) {
        console.warn('No hay sitios con subsitios/vehículos para exportar a PDF.');
        return;
      }

      const visibleColumns = Object.keys(VEHICULOS_SCHEMA);
      const meta = { estado: selectedEstado?.nombre || '' };

      exportEstadoPDFPorSitio(utiles, visibleColumns, meta);
    } catch (error) {
      console.error('Error en exportación PDF (estado):', error);
    }
  }, [sitiosDelEstado, vehiculosValidos, selectedEstado]);

  /**
   * Helper para generar breakdown de marcas
   */
  const getMarcasBreakdown = (vehiculosList) => {
    if (!vehiculosList?.length) return {};
    const marcasCount = {};
    for (let i = 0; i < vehiculosList.length; i++) {
      const marca = vehiculosList[i].Marca;
      marcasCount[marca] = (marcasCount[marca] || 0) + 1;
    }
    return Object.fromEntries(
      Object.entries(marcasCount).sort((a, b) => b[1] - a[1])
    );
  };

  const marcasEnMovimientoData = useMemo(() =>
    getMarcasBreakdown(vehiculosCategorizados.enMovimiento),
    [vehiculosCategorizados.enMovimiento]
  );

  const marcasEntregadosData = useMemo(() =>
    getMarcasBreakdown(vehiculosCategorizados.entregados),
    [vehiculosCategorizados.entregados]
  );

  const marcasPorLlegarData = useMemo(() =>
    getMarcasBreakdown(vehiculosCategorizados.porLlegar),
    [vehiculosCategorizados.porLlegar]
  );

  const marcasFaltantesData = useMemo(() =>
    getMarcasBreakdown(vehiculosCategorizados.faltantes),
    [vehiculosCategorizados.faltantes]
  );

  const estadisticas = useMemo(() => {
    const { faltantes, enMovimiento, porLlegar, entregados } = vehiculosCategorizados;
    // Restamos las entregadas (facturadas no entregadas) del total de inventario
    const inventarioTotal = (vehiculosValidos?.length || 0) - (entregados?.length || 0);

    // Cálculo de tooltip basado en marcasData para coincidir con Sidebar
    const tooltipInventario = Array.isArray(marcasData) ? marcasData.reduce((acc, m) => acc + (m.inventario || 0), 0) : 0;
    const tooltipPorLlegar = Array.isArray(marcasData) ? marcasData.reduce((acc, m) => acc + (m.enTransito || 0), 0) : 0;
    const tooltipFacturados = Array.isArray(marcasData) ? marcasData.reduce((acc, m) => acc + (m.facturadosNoEntregados || 0), 0) : 0;

    return [
      {
        title: 'Inventario total',
        value: inventarioTotal,
        icon: Car,
        color: 'bg-[#1d2e61]',
        isAlert: false,
        showMarcasBreakdown: true,
        marcasData: marcasInventarioData,
        detailedMarcasData: marcasData,
        iconPosition: 'top',
        type: null,
        calculationTooltip: {
          title: 'Total de vehículos',
          totalValue: tooltipInventario + tooltipPorLlegar + tooltipFacturados,
          items: [
            { label: 'Inventario', value: tooltipInventario, icon: Package, color: 'text-[#1d2e61]' },
            { label: 'C. por llegar', value: tooltipPorLlegar, icon: Activity, color: 'text-[#1d2e61]' },
            { label: 'Facturados', value: tooltipFacturados, icon: ShoppingCart, color: 'text-pink-500' }
          ]
        }
      },
      {
        title: 'Unidades en salida',
        value: entregados.length,
        icon: AlertTriangle,
        color: 'bg-[#1d2e61]',
        isAlert: false,
        isClickable: entregados.length > 0,
        showMarcasBreakdown: true,
        marcasData: marcasEntregadosData,
        detailedMarcasData: marcasData,
        type: 'entregados'
      },
      {
        title: 'Inventario en movimiento',
        value: enMovimiento.length,
        icon: TrendingUp,
        color: 'bg-[#1d2e61]',
        showCarAnimation: true,
        showMarcasBreakdown: true,
        marcasData: marcasEnMovimientoData,
        detailedMarcasData: marcasData,
        isAlert: false,
        isClickable: enMovimiento.length > 0,
        type: 'movimiento'
      },
      {
        title: 'Unidades por arribar',
        value: porLlegar.length,
        icon: Activity,
        color: 'bg-[#1d2e61]',
        isAlert: false,
        isClickable: porLlegar.length > 0,
        showMarcasBreakdown: true,
        marcasData: marcasPorLlegarData,
        detailedMarcasData: marcasData,
        type: 'porLlegar',
        facturadasPorLlegarCount: porLlegar.filter(v => esFacuradoSinFechaRecibo(v)).length,
        inventarioPorLlegarCount: porLlegar.filter(v => !esFacuradoSinFechaRecibo(v)).length
      },
      {
        title: 'Unidades no detectadas por RFID',
        value: faltantes.length,
        icon: Car,
        color: 'bg-red-500',
        isAlert: true,
        isClickable: faltantes.length > 0,
        showMarcasBreakdown: true,
        marcasData: marcasFaltantesData,
        detailedMarcasData: marcasData,
        type: 'faltantes',
        facturadasCount: faltantes.filter(v => v.estatus === ESTATUS.FACTURADO_NO_ENTREGADO).length,
        controlFacturadasCount: faltantes.filter(v => v.estatus === ESTATUS.ENTREGADO_STANDBY).length,
        inventarioSinEscanearCount: faltantes.filter(v => v.estatus === ESTATUS.INVENTARIO).length
      }
    ];
  }, [vehiculosValidos?.length, vehiculosCategorizados, marcasInventarioData, marcasEnMovimientoData, marcasEntregadosData, marcasPorLlegarData, marcasFaltantesData, marcasData]);

  useEffect(() => {
    preloadStatCardStyles();
  }, []);

  if (loading && !vehiculosPermitidos?.length) {
    return <LoadingScreen userInfo={userInfo} />;
  }

  if (error && !vehiculosPermitidos?.length) {
    return <ErrorScreen error={error} onRetry={handleManualRefresh} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar
        isExpandedExternal={uiState.sidebarExpanded}
        onExpandChange={handleSidebarExpandChange}
        onMarcaClick={handleMarcaClick}
        onUserManagementClick={handleUserManagementClick}
        onPatiosManagementClick={handlePatiosManagementClick}
        onRoleManagementClick={handleRoleManagementClick}
        onVendorsManagementClick={handleVendorsManagementClick}
        onSimulateLectureClick={handleSimulateLectureClick}
        onStatisticClick={handleStatisticClick}
        onUnidadesNoMayoreadasClick={handleUnidadesNoMayoreadasClick}
        onFacturadasNoEntregadasClick={handleFacturadasNoEntregadasClick}
        onHistoryClick={handleHistoryClick}
      />

      {/* Mobile menu button — top-right, visible only on small screens */}
      {!uiState.sidebarExpanded && (
        <button
          onClick={() => handleSidebarExpandChange(true)}
          className="fixed top-3 right-3 z-40 p-2 bg-white rounded-lg shadow-md sm:hidden"
          aria-label="Abrir menú"
        >
          <Menu className="w-5 h-5 text-gray-700" />
        </button>
      )}

      <div
        className={`transition-all duration-300 ease-in-out p-3 sm:p-4 lg:p-6 ${uiState.sidebarExpanded ? 'sm:ml-80' : 'sm:ml-16'
          }`}
        onClick={handleMainContentClick}
      >
        <div className="max-w-none px-1 sm:px-2 lg:px-4" style={{ textAlign: 'left' }}>

          <Header
            onLogout={logout}
            onRefresh={handleManualRefresh}
            isUpdating={isUpdating || isManualRefreshing}
            lastUpdateTime={lastUpdateTime}
            secondaryActionLabel="Trazabilidad"
            onSecondaryAction={handleGoToTraceability}
            secondaryIcon={Activity}
          />

          <DashboardLegend />

          <div className={`mb-8 transition-all duration-300 ease-out ${uiState.isStatCardAnimating ? 'opacity-90 scale-98' : 'opacity-100 scale-100'
            }`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 auto-rows-fr">
              {estadisticas.map((stat) => (
                <MemoizedStatCard
                  key={stat.title}
                  {...stat}
                  onClick={stat.type ? () => showPopup(stat.type) : undefined}
                />
              ))}
            </div>
          </div>

          <MemoizedFiltersSection
            vehiculos={vehiculosValidos}
            loading={loading}
            onFiltersChange={handleFiltersChange}
            onResultsChange={() => { }}
            ref={filtersRef}
            externalMarca={externalMarca}
            externalStatisticType={externalStatisticType}
            marcasData={marcasData}
          />

          

          <div className="bg-white rounded-xl shadow-lg p-6 mb-6 relative overflow-hidden min-h-[800px]">

            {viewMode === 'estados' && (
              <div className="transition-all duration-300 ease-out">
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-gray-900">Sitios por Estado</h2>
                  <p className="text-gray-600 mt-1">
                    Total: {estadosFiltrados.reduce((sum, estado) => sum + estado.totalVehiculos, 0)} vehículos en patio con lectura RFID en {estadosFiltrados.length} estados
                  </p>
                </div>

                <div className="mb-6">
                  <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Buscar estado..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {estadosFiltrados.map((estado) => (
                    <EstadoCard
                      key={estado.nombre}
                      estado={estado}
                      onEstadoClick={handleEstadoClick}
                    />
                  ))}
                </div>

                {estadosFiltrados.length === 0 && (
                  <div className="text-center py-12">
                    <MapPin className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium text-gray-900">
                      {searchTerm
                        ? 'No se encontraron estados que coincidan con tu búsqueda'
                        : 'No hay vehículos registrados en ningún estado'}
                    </p>
                    {searchTerm && (
                      <p className="text-sm text-gray-500 mt-2">
                        Intenta con otro término de búsqueda
                      </p>
                    )}
                    {!searchTerm && (
                      <p className="text-sm text-gray-500 mt-2">
                        Los estados aparecerán cuando haya vehículos con lectura RFID
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {viewMode === 'sitios' && selectedEstado && (
              <div className="transition-all duration-300 ease-out">
                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
                  <button
                    onClick={() => setViewMode('estados')}
                    className="hover:text-gray-900 transition-colors"
                  >
                    Estados
                  </button>
                  <span>/</span>
                  <span className="text-gray-900 font-medium">{selectedEstado.nombre}</span>
                </div>

                <div className="mb-4 sm:mb-6">
                  <div className="flex items-start gap-2 sm:gap-4">
                    <button
                      onClick={handleBackFromSitios}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors shrink-0 mt-0.5"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <div className="min-w-0">
                      <h2 className="text-base sm:text-xl font-bold text-gray-900 flex items-center">
                        <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mr-2 shrink-0" />
                        <span className="truncate">Sitios de {selectedEstado.nombre}</span>
                      </h2>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 sm:mt-2 text-xs sm:text-sm text-gray-600">
                        <span className="flex items-center">
                          <Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          {selectedEstado.totalSitios} sitios
                        </span>
                        <span className="flex items-center">
                          <Car className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          {selectedEstado.totalVehiculos} vehículos total
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-6 flex items-center gap-4">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Buscar sitio..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={handleExcelClickEstado}
                      className="h-10 flex items-center gap-2 px-3 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors border border-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Exportar todos los sitios del estado a Excel"
                      aria-label="Exportar Excel"
                      disabled={!sitiosDelEstado?.some(s => !s.estaVacio)}
                    >
                      <FileDown className="w-4 h-4" />
                    </button>

                    <button
                      onClick={handlePdfClickEstado}
                      className="h-10 flex items-center gap-2 px-3 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Exportar todos los sitios del estado a PDF"
                      aria-label="Exportar PDF"
                      disabled={!sitiosDelEstado?.some((s) => !s.estaVacio)}
                    >
                      <FileText className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {sitiosFiltrados.map((sitio) => (
                    <SitioCard
                      key={sitio.nombre}
                      sitio={sitio}
                      onSitioClick={handleSitioClick}
                    />
                  ))}
                </div>

                {sitiosFiltrados.length === 0 && (
                  <div className="text-center py-12">
                    <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium text-gray-900">
                      {searchTerm
                        ? 'No se encontraron sitios que coincidan con tu búsqueda'
                        : `No hay vehículos en sitios de ${selectedEstado.nombre}`}
                    </p>
                    {searchTerm && (
                      <p className="text-sm text-gray-500 mt-2">
                        Intenta con otro término de búsqueda
                      </p>
                    )}
                    {!searchTerm && (
                      <p className="text-sm text-gray-500 mt-2">
                        Los sitios aparecerán cuando haya vehículos con lectura RFID
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {viewMode === 'subsitios' && selectedSitio && (
              <div className="transition-all duration-300 ease-out">
                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
                  <button
                    onClick={() => setViewMode('estados')}
                    className="hover:text-gray-900 transition-colors"
                  >
                    Estados
                  </button>
                  <span>/</span>
                  <button
                    onClick={() => setViewMode('sitios')}
                    className="hover:text-gray-900 transition-colors"
                  >
                    {selectedEstado?.nombre}
                  </button>
                  <span>/</span>
                  <span className="text-gray-900 font-medium">{selectedSitio.nombre}</span>
                </div>

                <div className="mb-4 sm:mb-6">
                  <div className="flex items-start gap-2 sm:gap-4">
                    <button
                      onClick={handleBackFromSubSitios}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors shrink-0 mt-0.5"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <div className="min-w-0">
                      <h2 className="text-base sm:text-xl font-bold text-gray-900 flex items-center">
                        <Package className="w-4 h-4 sm:w-5 sm:h-5 mr-2 shrink-0" />
                        <span className="truncate">Subsitios de {selectedSitio.nombre}</span>
                      </h2>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 sm:mt-2 text-xs sm:text-sm text-gray-600">
                        <span className="flex items-center">
                          <Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          {subSitiosDelSitio.length} subsitios
                        </span>
                        <span className="flex items-center">
                          <Car className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          {selectedSitio.totalVehiculos} vehículos total
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getSitioCategoryColor(selectedSitio.categoria)}`}>
                          {selectedSitio.categoria}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-6 flex items-center gap-4">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Buscar subsitio..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={handleExcelClickSitio}
                      className="h-10 flex items-center gap-2 px-3 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors border border-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Exportar todos los subsitios del sitio a Excel"
                      aria-label="Exportar Excel (estado)"
                      disabled={(sitiosDelEstado || []).every(s => s.estaVacio)}
                    >
                      <FileDown className="w-4 h-4" />
                    </button>

                    <button
                      onClick={handlePdfClickSitio}
                      className="h-10 flex items-center gap-2 px-3 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Exportar todos los subsitios del sitio a PDF"
                      aria-label="Exportar PDF (sitio)"
                      disabled={(subSitiosDelSitio || []).every((s) => s.estaVacio)}
                    >
                      <FileText className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {subSitiosFiltrados.map((subSitio) => (
                    <SubSitioCard
                      key={`${subSitio.nombre}-${subSitio.sitio}`}
                      subSitio={subSitio}
                      onSubSitioClick={handleSubSitioClick}
                    />
                  ))}
                </div>

                {subSitiosFiltrados.length === 0 && (
                  <div className="text-center py-12">
                    <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium text-gray-900">
                      {searchTerm
                        ? 'No se encontraron subsitios que coincidan con tu búsqueda'
                        : `No hay vehículos en subsitios de ${selectedSitio.nombre}`}
                    </p>
                    {searchTerm && (
                      <p className="text-sm text-gray-500 mt-2">
                        Intenta con otro término de búsqueda
                      </p>
                    )}
                    {!searchTerm && (
                      <p className="text-sm text-gray-500 mt-2">
                        Los subsitios aparecerán cuando haya vehículos con lectura RFID
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {viewMode === 'detalle' && (selectedSitio || selectedSubSitio) && (
              <div className="transition-all duration-300 ease-out">
                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
                  <button
                    onClick={() => setViewMode('estados')}
                    className="hover:text-gray-900 transition-colors"
                  >
                    Estados
                  </button>
                  <span>/</span>
                  <button
                    onClick={() => setViewMode('sitios')}
                    className="hover:text-gray-900 transition-colors"
                  >
                    {selectedEstado?.nombre}
                  </button>
                  <span>/</span>
                  {selectedSubSitio ? (
                    <>
                      <button
                        onClick={() => setViewMode('subsitios')}
                        className="hover:text-gray-900 transition-colors"
                      >
                        {selectedSitio?.nombre}
                      </button>
                      <span>/</span>
                      <span className="text-gray-900 font-medium">{selectedSubSitio.nombre}</span>
                    </>
                  ) : (
                    <span className="text-gray-900 font-medium">{selectedSitio?.nombre}</span>
                  )}
                </div>

                {selectedSubSitio ? (
                  <PatioCard
                    key={`subsitio-${selectedSubSitio.nombre}-${vehiculosHash}`}
                    subSitio={selectedSubSitio}
                    vehiculos={vehiculosDelSubSitio}
                    onBackClick={handleBackFromDetalle}
                  />
                ) : (
                  <MemoizedPatioCard
                    key={`sitio-${selectedSitio.nombre}-${vehiculosHash}`}
                    sitio={selectedSitio}
                    vehiculos={vehiculosDelSitio}
                    onBackClick={handleBackFromDetalle}
                    showDetail={true}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Suspense fallback={null}>
        {uiState.showStatusPopup && uiState.currentStatusType === 'entregados' && (
          <ControlFacturadasPopup
            vehiculos={vehiculosCategorizados.entregados}
            isOpen={uiState.showStatusPopup}
            onClose={closePopup}
          />
        )}

        {uiState.showStatusPopup && uiState.currentStatusType !== 'entregados' && (
          <VehicleStatusPopup
            vehiculos={vehiculosValidos}
            isOpen={uiState.showStatusPopup}
            onClose={closePopup}
            statusType={uiState.currentStatusType}
            vehiculosFiltrados={{
              ...vehiculosCategorizados,
              movimiento: vehiculosCategorizados.enMovimiento
            }}
          />
        )}

        <SidebarPopupContainer
          activePopup={activePopup}
          onClose={() => setActivePopup(null)}
          facturadasInitialMarca={facturadasInitialMarca}
        />
      </Suspense>
    </div>
  );
};

export default React.memo(Dashboard);
